import { property } from 'lit-element';
import { register } from '../../shared/base';
import { HeadlessExpander } from '../headless/expander';
import styles from './navigation-section.lit.scss';

/**
 * @component {kat-navigation-section} KatalNavigationSection This component is used in the vertical kat-navigation component to allow you to create a single level of nested links.
 * @status Production
 * @theme flo
 * @guideline Do Only use this component as a part of kat-navigation in the vertical layout. It does not provide any other option.
 * @guideline Do Ensure your label accurately reflects all of the links underneath, even when collapsed.
 * @guideline Dont Don’t use the default <code>expander</code> instead of this component.
 * @example Vertical { "content": "<div>Info Button with Icon</div>" }
 * @slot default Contents will be used as the main collapsible content section.
 * @category Container
 */
@register('kat-navigation-section')
export class KatNavigationSection extends HeadlessExpander {
  static styles = [...HeadlessExpander.styles, styles];

  @property()
  private hz: false;

  firstUpdated() {
    super.firstUpdated();

    const observer = new MutationObserver(mutations => {
      const children = mutations
        .filter(m => m.target !== this)
        .map(m => m.target as HTMLElement);

      if (children.length !== 0) {
        this._handleActive(children);
      }
    });
    observer.observe(this, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-active'],
    });

    this._handleActive(Array.from(this.children) as HTMLElement[]);
  }

  private _handleActive(children: HTMLElement[]) {
    const hasActiveChild = children.some(child =>
      child.hasAttribute('data-active')
    );

    if (hasActiveChild) {
      this.dataset.active = '';
    } else {
      delete this.dataset.active;
    }
  }
}
